import React, { useEffect, useState } from 'react';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { TriangleAlert } from 'lucide-react';
import { BroadcastEventType } from '@cotera/api';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading, toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  ResourceSelectionModel,
  ResourceType,
} from '../resources/selection/resource-selection.model';
import { SchedulePicker } from '../trigger/schedule-picker';
import {
  formatNextRun,
  getNextRun,
  humanReadableSchedule,
} from '../trigger/schedule-utils';
import { isWarehouseDataset } from './dataset-utils';
import { DatasetModel } from './dataset.model';

/**
 * What a dataset falls back to when it is put on a schedule for the first time.
 * The same daily 09:00 the create form offers, so a schedule turned on here and
 * one chosen at create start from the same place.
 */
const DEFAULT_CADENCE = '0 9 * * *';

const setChangeDetectionScheduleParamsSchema = z.object({
  /** Cron the dataset is re-scanned on, or null for no schedule. */
  cadence: z.string().min(1).nullable(),
  // A resource list row already knows which dataset it targets, so it can pass
  // the id directly rather than relying on the shared selection model.
  datasetId: z.string().optional(),
});

type Params = z.infer<typeof setChangeDetectionScheduleParamsSchema>;

type Errors = { t: string; message?: string }[];

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

type SetChangeDetectionScheduleActionClient = {
  resource: {
    dataset: Pick<TenantedClient['resource']['dataset'], 'updateSettings'>;
  };
};

/**
 * Changes how often a dataset is re-scanned for changes, after it was created.
 *
 * The schedule was previously only choosable in the create form, which left a
 * dataset on whatever cadence it was given — a warehouse table that turned out
 * to change hourly could not be moved off a daily scan without being rebuilt.
 * Writing it goes through the settings route, which reconciles the Restate poll
 * job: the cron is set up, rescheduled or cancelled to match.
 *
 * The timezone is left alone. The picker works in UTC, as it does everywhere
 * else in the product, and a dataset given another timezone by the API keeps it
 * — this only moves the cron.
 */
export class SetChangeDetectionScheduleAction extends BaseAction<
  Params,
  Errors
> {
  title = 'Change detection schedule';
  description = 'Set how often this dataset is re-scanned for changes';
  inputSchema = setChangeDetectionScheduleParamsSchema;
  group = 'Dataset';
  icon = 'clock';

  constructor(
    private readonly client: SetChangeDetectionScheduleActionClient,
    private readonly modalManager: ModalManager,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTargetDatasetId(context) !== null;
  }

  shouldAsk(): boolean {
    return true;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<Errors>> {
    const datasetId = payload.datasetId ?? this.getTargetDatasetId(context);
    if (datasetId === null || datasetId === undefined) {
      return err([{ t: 'error', message: 'No dataset in scope' }]);
    }

    const result = await this.client.resource.dataset.updateSettings({
      id: datasetId,
      cadence: payload.cadence,
    });
    if (result.isErr()) {
      const message =
        result.error.t === 'application'
          ? result.error.err.message ?? 'Failed to save the schedule'
          : 'Failed to save the schedule';
      toast.error(message);
      return err([{ t: 'error', message }]);
    }

    emit(BroadcastEventType.ResourceUpdated, {
      orgId: this.orgId,
      resourceType: 'dataset',
      resourceId: datasetId,
    });

    toast.success(
      payload.cadence === null
        ? 'Change detection schedule turned off'
        : `Checking for changes: ${humanReadableSchedule(payload.cadence)}`
    );
    return ok({});
  }

  ask(params: Partial<Params>, context: ExecuteContext): AskResponse<Params> {
    // Resolved before the modal opens, for the same reason the row-keys action
    // resolves it here: the shared selection can move while the dialog is up.
    const datasetId = params.datasetId ?? this.getTargetDatasetId(context);
    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async (): Promise<void> => {
          await onCancel();
          this.modalManager.pop();
        };
        this.modalManager.push({
          view: () => (
            <SetChangeDetectionScheduleAskView
              datasetId={datasetId}
              onRespond={async (nextParams) => {
                await onRespond({
                  ...nextParams,
                  ...(datasetId !== null ? { datasetId } : {}),
                });
                this.modalManager.pop();
              }}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }

  private selectedDataset(
    context: ApplicableContext
  ): ResourceSelectionModel | null {
    if (!context.isInScope(ResourceSelectionModel)) {
      return null;
    }
    const selection = context.getInScopeModelsOfType(ResourceSelectionModel);
    const single = selection.length === 1 ? selection[0] : undefined;
    if (
      single === undefined ||
      single.selections.length !== 1 ||
      single.getResourceIdsForType(ResourceType.Dataset).length !== 1
    ) {
      return null;
    }
    return single;
  }

  private getTargetDatasetId(context: ApplicableContext): string | null {
    const selected = this.selectedDataset(context);
    if (selected !== null) {
      return selected.getResourceIdsForType(ResourceType.Dataset)[0] ?? null;
    }
    const datasetModel = context.targetedModelOfType(DatasetModel);
    return datasetModel?.datasetId ?? null;
  }
}

/**
 * Picks the new cadence, starting from the one the dataset is already on.
 *
 * Fetches the dataset rather than reading a model, so the same dialog works
 * from the dataset page, a resource list row and the command palette alike —
 * and so it opens showing the real saved schedule instead of a default that
 * would overwrite it if the user only came to look.
 */
const SetChangeDetectionScheduleAskView: React.FC<{
  datasetId: string | null;
  onRespond: (params: Params) => Promise<void>;
  onCancel: () => Promise<void>;
}> = ({ datasetId, onRespond, onCancel }) => {
  const client = useTenantedClient();
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [cadence, setCadence] = useState(DEFAULT_CADENCE);
  /**
   * Whether clearing the schedule stops change detection outright. A warehouse
   * table is written by the customer's own ETL and announces nothing, so the
   * scan is the only way its changes are ever seen; csv and manual announce
   * their own writes, and a schedule there is a sweeper for a missed one.
   */
  const [scheduleRequired, setScheduleRequired] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (datasetId === null) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await client.resource.get({
        type: ResourceType.Dataset,
        id: datasetId,
      });
      if (cancelled) {
        return;
      }
      if (res.isErr() || res.value.type !== 'dataset') {
        setLoadError('Could not load this dataset');
        return;
      }
      const saved = res.value.dataset.cadence ?? null;
      setEnabled(saved !== null);
      // A dataset with no schedule still needs something in the picker for
      // turning one on, so the default stands in until it does.
      setCadence(saved ?? DEFAULT_CADENCE);
      setScheduleRequired(isWarehouseDataset(res.value.dataset.type));
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, datasetId]);

  const submittable = enabled ? cadence.trim() !== '' : true;
  const canSubmit = !saving && loaded && datasetId !== null && submittable;

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) {
      return;
    }
    setSaving(true);
    await onRespond({ cadence: enabled ? cadence.trim() : null });
    setSaving(false);
  };

  return (
    <ModalPageView
      title="Change detection schedule"
      subtitle="How often this dataset is re-scanned for changes"
      className="px-4 py-4"
    >
      <div className="flex flex-col gap-4">
        {loadError !== null ? (
          <p className="text-sm text-destructive">{loadError}</p>
        ) : !loaded ? (
          <div className="flex justify-center py-6">
            <Loading.Spinner variant="sm" />
          </div>
        ) : (
          <>
            <label className="flex cursor-pointer items-center gap-3 rounded-md border border-input px-3 py-2 text-sm hover:bg-muted/40">
              <Checkbox
                checked={enabled}
                onCheckedChange={() => setEnabled((current) => !current)}
              />
              <span className="min-w-0 flex-1">
                <span className="font-medium">Check on a schedule</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  every tick re-scans the source and fires this dataset&rsquo;s
                  triggers
                </span>
              </span>
            </label>

            {enabled ? (
              <div className="flex flex-col gap-2">
                <SchedulePicker value={cadence} onChange={setCadence} />
                <NextRun cadence={cadence} />
              </div>
            ) : (
              <ScheduleOffWarning required={scheduleRequired} />
            )}
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void onCancel()}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            {saving ? <Loading.Spinner variant="sm" /> : 'Save schedule'}
          </Button>
        </div>
      </div>
    </ModalPageView>
  );
};

/**
 * The schedule read back in words, plus when it next fires. A custom cron is
 * the one thing a user cannot check by eye, and this is where a typo shows up
 * before it is saved.
 */
const NextRun: React.FC<{ cadence: string }> = ({ cadence }) => {
  const trimmed = cadence.trim();
  if (trimmed === '') {
    return (
      <span className="text-xs text-muted-foreground">
        Enter a cron expression.
      </span>
    );
  }
  const next = getNextRun(trimmed);
  if (next === null) {
    return (
      <span className="text-xs text-destructive">
        That is not a cron expression this can read.
      </span>
    );
  }
  return (
    <span className="text-xs text-muted-foreground">
      {humanReadableSchedule(trimmed)} — next {formatNextRun(next)}.
    </span>
  );
};

/** What turning the schedule off actually costs, which differs by kind. */
const ScheduleOffWarning: React.FC<{ required: boolean }> = ({ required }) =>
  required ? (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700">
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        A warehouse table changes upstream with no event, so the scan is the
        only way those changes are seen. With no schedule, this dataset is only
        re-checked when you run Detect changes yourself, and nothing watching it
        will fire in between.
      </span>
    </div>
  ) : (
    <span className="text-xs text-muted-foreground">
      Writes to this dataset announce themselves, so triggers still fire without
      a schedule. The scan is a sweeper for a write that went unnoticed — and
      the only way a deleted row is reconciled on a dataset nobody is writing
      to.
    </span>
  );
